import { createContext, useCallback, useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'
import createAuthRefreshInterceptor from 'axios-auth-refresh'
import { Maybe } from 'yup/lib/types'

import { selectAccessToken, setTokenPair, clearTokens, logoutUser } from 'api/reduxSlices/auth'
import { useContextWithCheck } from 'hooks'
import { Token, TokenType } from 'api/domain/auth'
import { tokenToString } from 'api/utils'
import { useAppApiContext } from 'api/providers/AppApiProvider'

import { AuthApi } from '../../APIs'

import { AuthProviderProps, AuthContextProps } from './index'

const AuthContext = createContext<AuthContextProps | undefined>(undefined)

/**
 * AuthProvider is used as a supplement to the AppApi context.
 * It provides methods to make login, logout and to refresh the session (which is called initially on mount to make autologin).
 */
export const AuthProvider = ({ children, onLogout }: AuthProviderProps): JSX.Element => {
  const queryClient = useQueryClient()
  const { apiInstance } = useAppApiContext()

  const [isReady, setIsReady] = useState<boolean>(false)

  const [isLoading, setIsLoading] = useState(true)
  const checkToStart = (tkn: Maybe<TokenType>) => {
    const ready = !!tkn
    setIsReady(ready)
  }

  /**
   * Login method
   */
  const login = useCallback(async (newTokens: Token) => {
    await setTokenPair(newTokens)
    checkToStart(newTokens.access_token)
  }, [])

  /**
   * Logout method.
   * Resets the access and refresh tokens, clears react-query cache.
   */
  const logout = useCallback(async () => {
    onLogout?.()
    clearTokens()
    setIsReady(false)
    logoutUser()
    queryClient.clear()
  }, [onLogout, queryClient])

  /**
   * Refresh session method
   */
  const refreshSession = useCallback(() => {
    return new AuthApi(apiInstance).refresh().then(newTokens => {
      setTokenPair(newTokens)
      checkToStart(newTokens.access_token)
      return newTokens
    })
  }, [apiInstance])

  /**
   * Add authorization token to each request
   */
  useEffect(() => {
    apiInstance.axiosClient.interceptors.request.use(request => {
      const accessToken = selectAccessToken()
      if (accessToken) {
        // @ts-ignore
        request.headers = {
          ...request.headers,
          Authorization:
            request.url === '/auth/refresh'
              ? request.headers?.Authorization || tokenToString(accessToken)
              : tokenToString(accessToken),
        }
      }
      return request
    })
  }, [apiInstance])

  /**
   * Setup axios-auth-refresh
   */
  useEffect(() => {
    createAuthRefreshInterceptor(
      apiInstance.axiosClient,
      failedRequest => {
        return refreshSession()
          .then(({ access_token }) => {
            if (failedRequest) {
              failedRequest.response.config.headers['Authorization'] = tokenToString(access_token)
            }
          })
          .catch(error => {
            clearTokens()
            setIsReady(false)
            console.error('%o', error)
          })
      },
      {
        statusCodes: [401],
        // pauseInstanceWhileRefreshing: true,
        // shouldRefresh: error => error?.response?.data?.error_code === 89,
      },
    )
  }, [apiInstance, refreshSession])

  useEffect(() => {
    const tkn = selectAccessToken()
    try {
      checkToStart(tkn)
    } finally {
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Make autologin with refresh token on mount
   */
  // useEffect(() => {
  //   refreshSession()
  //     .catch()
  //     .finally(() => setIsLoading(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [])

  return (
    <AuthContext.Provider
      value={{
        isReady,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

/**
 * Hook to access AuthContext
 */
export const useAuth = () => useContextWithCheck(AuthContext)
